import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import FormField from '../components/FormField';
import RegionPicker from '../components/RegionPicker';
import CompensationSchemaPanel from '../components/CompensationSchemaPanel';
import { useFormDirty } from '../hooks/useFormDirty';
import { toast } from '../utils/toast';

/* ── SVG Icons (monochrome, 18px) ── */
const IconView = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconEdit = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconDelete = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);
const IconBack = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);
const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const IconLetter = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);
const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const IconMail = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/>
  </svg>
);
const IconUpload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

/* ── Constants ── */
const STATUS_BADGES = {
  Active: 'badge-green', Resigned: 'badge-yellow', Terminated: 'badge-red',
  Absconding: 'badge-red', Offered: 'badge-blue', OfferRejected: 'badge-gray',
  Joining: 'badge-blue', Backout: 'badge-gray', Inactive: 'badge-gray',
};
const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Widowed'];
const EMP_TYPE_OPTIONS = ['Full-Time', 'Contractual', 'Intern'];
const STATUS_OPTIONS = ['Active', 'Resigned', 'Terminated', 'Absconding', 'Offered', 'OfferRejected', 'Joining', 'Backout', 'Inactive'];
const PROBATION_OPTIONS = ['On Probation', 'Confirmed', 'Extended'];
const YES_NO_OPTIONS = ['Yes', 'No'];
const DOC_STATUS_OPTIONS = ['Yes', 'No', 'Pending', 'N/A'];
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const TSHIRT_SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const PF_INTEREST_OPTIONS = ['Yes', 'No'];

const TAB_LIST = [
  { key: 'personal', label: 'Personal Information' },
  { key: 'contact', label: 'Contact Details' },
  { key: 'transit', label: 'Transit Details' },
  { key: 'employment', label: 'Employment Details' },
  { key: 'compensation', label: 'Compensation Details' },
  { key: 'documents', label: 'Document Status' },
  { key: 'certification', label: 'SAP Certification' },
  { key: 'skills', label: 'Skills' },
  { key: 'experience', label: 'Experience' },
  { key: 'projects', label: 'Project Experience' },
  { key: 'assets', label: 'Assets & Other' },
];

/* ── Inline Table Icons ── */
const IconCopy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);
const IconTrashSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
  </svg>
);
const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

/* ── Inline editable table for sub-records ── */
function InlineTable({ columns, rows, onChange, onAdd, onDelete, onCopy, readOnly, tableName }) {
  const cellStyle = { padding: '6px 8px', fontSize: 12.5, borderBottom: '1px solid rgba(255,255,255,0.04)' };
  const headerStyle = { ...cellStyle, fontSize: 11, fontWeight: 600, color: '#64748B', textTransform: 'uppercase',
    letterSpacing: '0.4px', background: 'rgba(255,255,255,0.03)', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 };
  const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 4, padding: '5px 8px', color: '#E2E8F0', fontSize: 12.5, outline: 'none' };
  const actionBtnStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: 5, border: 'none', cursor: 'pointer', transition: 'all 0.15s' };

  return (
    <div>
      <div style={{ overflowX: 'auto', overflowY: 'visible', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: columns.length * 130 }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, width: 36 }}>#</th>
              {columns.map(col => (
                <th key={col.key} style={{ ...headerStyle, minWidth: col.width || 120 }}>{col.label}</th>
              ))}
              {!readOnly && <th style={{ ...headerStyle, width: 70, textAlign: 'center' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (readOnly ? 1 : 2)} style={{ ...cellStyle, textAlign: 'center', color: '#64748B', padding: '24px 8px' }}>
                  No records yet.{!readOnly && ' Click "+ Add Row" to begin.'}
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr key={row._key || row.id || idx} style={{ background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <td style={{ ...cellStyle, color: '#64748B', fontSize: 11, textAlign: 'center' }}>{idx + 1}</td>
                {columns.map(col => (
                  <td key={col.key} style={cellStyle}>
                    {readOnly || col.type === 'readonly' ? (
                      <span style={{ color: col.type === 'readonly' && !readOnly ? '#94A3B8' : '#E2E8F0', fontSize: 12.5 }}>{row[col.key] || '—'}</span>
                    ) : col.type === 'select' ? (
                      <select style={inputStyle} value={row[col.key] || ''} onChange={e => onChange(idx, col.key, e.target.value)}>
                        <option value="">—</option>
                        {(typeof col.options === 'function' ? col.options(row) : (col.options || [])).map(o => {
                          const val = typeof o === 'object' ? o.value : o;
                          const label = typeof o === 'object' ? o.label : o;
                          return <option key={val} value={val}>{label}</option>;
                        })}
                      </select>
                    ) : col.type === 'number' ? (
                      <input type="number" style={inputStyle} value={row[col.key] || ''} onChange={e => onChange(idx, col.key, e.target.value)} />
                    ) : col.type === 'date' ? (
                      <input type="date" style={inputStyle} value={row[col.key] || ''} onChange={e => onChange(idx, col.key, e.target.value)} />
                    ) : (
                      <input type="text" style={inputStyle} value={row[col.key] || ''} onChange={e => onChange(idx, col.key, e.target.value)} />
                    )}
                  </td>
                ))}
                {!readOnly && (
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                      <button title="Copy row" style={{ ...actionBtnStyle, background: 'rgba(96,165,250,0.1)', color: '#60A5FA' }}
                        onClick={() => onCopy(idx)}><IconCopy /></button>
                      <button title="Delete row" style={{ ...actionBtnStyle, background: 'rgba(220,38,38,0.1)', color: '#F87171' }}
                        onClick={() => onDelete(idx)}><IconTrashSmall /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={onAdd}
            style={{ fontSize: 12, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconPlus /> Add Row
          </button>
          {rows.some(r => r._duplicate) && (
            <span style={{ fontSize: 12, color: '#FBBF24', display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Duplicate rows detected (highlighted)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Skills table columns (static fallback — overridden by metadata inside component) ── */
const SKILL_COLUMNS_STATIC = [
  { key: 'skill_type', label: 'Type', width: 110, type: 'select', options: ['Functional', 'Technical', 'Management', 'Others'] },
  { key: 'product', label: 'Product', width: 130, type: 'select', options: ['Private Cloud', 'Public Cloud', 'BTP', 'SuccessFactors', 'Other'] },
  { key: 'module', label: 'Skill Group', width: 80 },
  { key: 'skillset', label: 'Skillset', width: 180 },
  { key: 'self_score', label: 'Self Score', width: 85, type: 'number' },
  { key: 'manager_score', label: 'Mgr Score', width: 85, type: 'number' },
  { key: 'max_score', label: 'Max Score', width: 85, type: 'number' },
];

const EXPERIENCE_COLUMNS = [
  { key: 'exp_type', label: 'Type', width: 110, type: 'select', options: ['Domain', 'Functional', 'Technical', 'Management', 'Others'] },
  { key: 'organization', label: 'Organization', width: 200 },
  { key: 'designation', label: 'Designation', width: 180 },
  { key: 'module', label: 'Skill Group', width: 80 },
  { key: 'from_date', label: 'From', width: 120, type: 'date' },
  { key: 'to_date', label: 'To', width: 120, type: 'date' },
  { key: 'verified', label: 'Verified', width: 80, type: 'select', options: ['Yes', 'No'] },
];

const PROJECT_COLUMNS = [
  { key: 'project_type', label: 'Type', width: 110, type: 'select', options: ['Domain', 'Functional', 'Technical', 'Management', 'Others'] },
  { key: 'organization', label: 'Organization', width: 180 },
  { key: 'role', label: 'Role', width: 110, type: 'select', options: ['Primary', 'Secondary'] },
  { key: 'module', label: 'Skill Group', width: 80 },
  { key: 'industry', label: 'Industry', width: 130 },
  { key: 'project_category', label: 'Project Type', width: 130, type: 'select', options: ['Implementation', 'Support', 'Migration', 'Rollout', 'Other'] },
  { key: 'customer', label: 'Customer', width: 130 },
  { key: 'from_date', label: 'From', width: 120, type: 'date' },
  { key: 'to_date', label: 'To', width: 120, type: 'date' },
  { key: 'concurrent', label: 'Concurrent', width: 100, type: 'select', options: ['Primary', 'Secondary'] },
];

function buildEmptyForm() {
  return {
    photo_path: '',
    emp_code: '', title: '', first_name: '', last_name: '', gender: '', date_of_birth: '', marital_status: '', nationality: 'Indian',
    phone: '', alternate_phone: '', personal_email: '', email: '',
    department_id: '', module: '', designation_id: '', reporting_to: '', employment_type: 'Full-Time',
    date_of_offer: '', date_of_joining: '', joining_status: '', probation_end_date: '', probation_status: '',
    current_exp_nouveau: '', appraisal_cycle: '', current_address: '', location: '', home_location: '', region_id: '',
    offer_shared_by: '', approved_by: '', final_approval: '',
    status: 'Active', exit_date: '', reason_for_exit: '', relieving_letter: '',
    offer_ctc_annual: '', revise_offer_date: '', revise_offer_salary: '',
    salary_schema_id: '',
    gross_pa_fixed: '', gross_pm_fx_var: '', basic_salary: '', hra: '', statutory_bonus: '', lta: '',
    personal_allowance: '', variable_pay: '', pm_amount: '', company_pf: '', employee_pf: '',
    professional_tax: '', bonus: '', net_pm: '',
    bank_name: '', bank_account_number: '', ifsc_code: '', pf_holder: '', interested_for_pf: '',
    pf_amt_withdrawn: '', pf_account_no: '', uan_number: '', old_uan_number: '', pf_wages_pm: '', pf_date_joining: '',
    pan_number: '', aadhaar_number: '', esic_number: '',
    pan_submitted: '', aadhaar_submitted: '', resume_submitted: '', edu_certificates_submitted: '',
    experience_letters_submitted: '', bank_statement_submitted: '', documents_completed: '',
    offer_letter_issued: '', appointment_letter_issued: '', bond: '', bond_duration: '', bond_sign: '',
    sap_certification: '', certificate_validity: '', credly_link: '',
    blood_group: '', laptop_charger: '', id_card: '', diary: '', pen: '', bag: '', tshirt: '',
    tshirt_size: '', lan_wire: '', car_bike: '', vehicle_no: '', tag_id: '', group_tag_id: '',
    expected_joining_date: '', confirmed_joining_date: '',
    transit_metropolis: '', transit_means: '', transit_line: '', transit_zone: '',
    transit_locality: '', transit_duration: '', local_address: '',
  };
}

function computeAge(dob) {
  if (!dob) return '';
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : '';
}

function computeExperience(dateOfJoining, exitDate) {
  if (!dateOfJoining) return '';
  const start = new Date(dateOfJoining);
  const end = exitDate ? new Date(exitDate) : new Date();
  if (isNaN(start) || isNaN(end) || end < start) return '';
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months -= 1;
  if (months < 0) return '';
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0) return rem + ' mo';
  if (rem === 0) return years + ' yr';
  return years + ' yr ' + rem + ' mo';
}

function flattenEmployee(emp) {
  if (!emp) return buildEmptyForm();
  const f = buildEmptyForm();
  for (const key of Object.keys(f)) {
    if (emp[key] !== undefined && emp[key] !== null) {
      const val = emp[key];
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
        f[key] = val.split('T')[0];
      } else {
        f[key] = val;
      }
    }
  }
  return f;
}

/* ── Styles ── */
const styles = {
  actionBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 7, border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)', color: '#94A3B8', cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  actionBtnDanger: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 7, border: '1px solid rgba(220,38,38,0.2)',
    background: 'rgba(220,38,38,0.06)', color: '#F87171', cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  headerCard: {
    background: 'rgba(255,255,255,0.03)', borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.06)', padding: '24px 28px',
    display: 'flex', alignItems: 'center', gap: 20, marginBottom: 0,
  },
  avatar: {
    width: 64, height: 64, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, fontWeight: 700, color: '#fff', flexShrink: 0,
    background: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
  },
  tabBar: {
    display: 'flex', gap: 0, borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)', borderRadius: '12px 12px 0 0',
    overflowX: 'auto', padding: '0 4px',
    /* Hide scrollbar across browsers */
    msOverflowStyle: 'none', scrollbarWidth: 'none',
  },
  tabBtn: (active) => ({
    padding: '14px 20px', fontSize: 13, fontWeight: active ? 600 : 400,
    color: active ? '#60A5FA' : '#94A3B8',
    background: active ? 'rgba(96,165,250,0.08)' : 'transparent',
    border: 'none', borderBottom: active ? '2px solid #60A5FA' : '2px solid transparent',
    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s ease',
  }),
  tabContent: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
    borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '24px 28px',
    minHeight: 300,
  },
  sectionHeading: {
    fontSize: 13, fontWeight: 600, color: '#60A5FA', marginTop: 24, marginBottom: 14,
    borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 8,
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  viewField: {
    marginBottom: 18,
  },
  viewLabel: {
    fontSize: 11, fontWeight: 500, color: '#64748B', textTransform: 'uppercase',
    letterSpacing: '0.5px', marginBottom: 4,
  },
  viewValue: {
    fontSize: 14, color: '#E2E8F0', fontWeight: 400, minHeight: 20,
  },
  deleteConfirm: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  },
  deleteBox: {
    background: '#1E293B', borderRadius: 12, padding: '28px 32px',
    border: '1px solid rgba(255,255,255,0.1)', maxWidth: 420, width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
};

/* ── View-only field renderer ── */
function ViewField({ label, value }) {
  const display = value === undefined || value === null || value === '' ? '—' : String(value);
  return (
    <div style={styles.viewField}>
      <div style={styles.viewLabel}>{label}</div>
      <div style={styles.viewValue}>{display}</div>
    </div>
  );
}

/* ── Main Component ── */
export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [offices, setOffices] = useState([]);
  const [grades, setGrades] = useState([]);
  const [salarySchemas, setSalarySchemas] = useState([]);
  const [transitData, setTransitData] = useState([]);
  // Group Tags master — populates the Group Tag dropdown on the form.
  // Loaded once via the workflow plugin route (same one GroupTags page uses).
  const [groupTags, setGroupTags] = useState([]);

  // Detail/edit state
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mode, setMode] = useState('list'); // 'list' | 'view' | 'edit' | 'create'
  const [form, setForm] = useState(buildEmptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusTab, setStatusTab] = useState('ACTIVE');
  const [filterDept, setFilterDept] = useState('');
  const [filterDesig, setFilterDesig] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [highlightEmpId, setHighlightEmpId] = useState(null);

  // Sub-table state (skills, experience, projects)
  const [skillRows, setSkillRows] = useState([]);
  const [expRows, setExpRows] = useState([]);
  const [projRows, setProjRows] = useState([]);
  const [subSaving, setSubSaving] = useState(false);
  const [subMsg, setSubMsg] = useState('');
  const tabScrollRef = React.useRef(null);
  const { dirty, markDirty, resetDirty, confirmIfDirty } = useFormDirty();

  // Metadata cascading lookups (Products → Modules → Skillsets)
  const [metaProducts, setMetaProducts] = useState([]);
  const [metaModules, setMetaModules] = useState([]);
  const [metaModuleProducts, setMetaModuleProducts] = useState([]); // junction M2M
  const [metaSkillsets, setMetaSkillsets] = useState([]);

  // Employee Skills selector state (for redesigned tab)
  const [skillSubTab, setSkillSubTab] = useState('Functional');
  const [skillFilterProducts, setSkillFilterProducts] = useState([]);
  const [skillFilterModules, setSkillFilterModules] = useState([]);
  const [skillProdDropOpen, setSkillProdDropOpen] = useState(false);
  const [skillModDropOpen, setSkillModDropOpen] = useState(false);
  const skillProdDropRef = React.useRef(null);
  const skillModDropRef = React.useRef(null);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await apiFetch('/api/employees?limit=500');
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.employees || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const [deptRes, desigRes, officeRes, gradeRes, transitRes, schemaRes, gtagRes] = await Promise.allSettled([
        apiFetch('/api/employees/departments'),
        apiFetch('/api/employees/designations'),
        apiFetch('/api/employees/offices'),
        apiFetch('/api/employees/grades'),
        apiFetch('/api/employees/transit'),
        apiFetch('/api/salary-schemas'),
        // Group Tags master — drives the Group Tag dropdown on the form.
        apiFetch('/api/workflow/group-tags'),
      ]);
      if (deptRes.status === 'fulfilled' && deptRes.value.ok) {
        const d = await deptRes.value.json();
        setDepartments(d.departments || []);
      }
      if (desigRes.status === 'fulfilled' && desigRes.value.ok) {
        const d = await desigRes.value.json();
        setDesignations(d.designations || []);
      }
      if (officeRes.status === 'fulfilled' && officeRes.value.ok) {
        const d = await officeRes.value.json();
        setOffices(d.offices || []);
      }
      if (gradeRes.status === 'fulfilled' && gradeRes.value.ok) {
        const d = await gradeRes.value.json();
        setGrades(d.grades || []);
      }
      if (transitRes.status === 'fulfilled' && transitRes.value.ok) {
        const d = await transitRes.value.json();
        setTransitData(d.transit || []);
      }
      if (schemaRes.status === 'fulfilled' && schemaRes.value.ok) {
        const d = await schemaRes.value.json();
        setSalarySchemas(d.schemas || []);
      }
      if (gtagRes && gtagRes.status === 'fulfilled' && gtagRes.value.ok) {
        const d = await gtagRes.value.json();
        // Show only Active tags so deactivated ones don't clutter the picker.
        setGroupTags((d.tags || []).filter(t => (t.status || 'Active') === 'Active'));
      }
    } catch (err) { /* handled */ }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  /* Detect admin / owner so we can unlock emp_code / first_name / last_name after create.
     Covered buckets:
       - Any role containing "admin"
       - "Super User"
       - Owner / Managing Director / Founder / CEO / Director / Partner
       - roleLevel >= 80 (leadership band)
       - The platform owner email (kazi@thenewequation.biz)
       - Any user linked to employee emp_code starting with "M001" (founders). */
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    apiFetch('/api/auth/me').then(r => r.ok ? r.json() : null).then(u => {
      if (!u) return;
      const roleName = String(u.roleName || u.role || '').toLowerCase();
      const email    = String(u.email || '').toLowerCase();
      const empCode  = String(u.empCode || u.emp_code || '').toUpperCase();
      const roleLevel = Number(u.roleLevel || 0);
      const leadershipRoles = ['admin', 'super user', 'owner', 'founder', 'managing director',
                               'director', 'ceo', 'partner', 'md'];
      const roleMatch = leadershipRoles.some(r => roleName.includes(r));
      const ownerEmail = email === 'kazi@thenewequation.biz' || email === 'sshkazi@gmail.com';
      const founderCode = empCode === 'M001';
      setIsAdmin(roleMatch || roleLevel >= 80 || ownerEmail || founderCode);
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchLookups(); }, [fetchLookups]);

  // Load metadata cascade for Skills dropdowns
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/metadata/cascade');
        if (res.ok) {
          const data = await res.json();
          setMetaProducts(data.products || []);
          setMetaModules(data.modules || []);
          setMetaModuleProducts(data.moduleProducts || []);
          setMetaSkillsets(data.skillsets || []);
        }
      } catch (_) { /* silent */ }
    })();
  }, []);

  // Build SKILL_COLUMNS for the redesigned skills table
  // Now simplified: columns are Product, Module, Skillset, Self Score, Max Score (read-only)
  const SKILL_COLUMNS = useMemo(() => {
    return [
      { key: 'product', label: 'Product', width: 140, type: 'readonly' },
      { key: 'module', label: 'Skill Group', width: 120, type: 'readonly' },
      { key: 'skillset', label: 'Skillset', width: 220, type: 'readonly' },
      { key: 'self_score', label: 'Self Score', width: 90, type: 'select',
        options: ['', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'L1', 'L2', 'L3', 'L4', 'L5'] },
      { key: 'max_score', label: 'Max Score', width: 90, type: 'readonly' },
    ];
  }, []);

  // Helper: get module IDs linked to a product via junction table
  function moduleIdsForProduct(productId) {
    return metaModuleProducts.filter(mp => mp.product_id === productId).map(mp => mp.module_id);
  }

  // Available modules filtered by selected products AND active skill sub-tab type
  const skillAvailableModules = useMemo(() => {
    let result = metaModules;
    // Filter by sub-tab type
    if (skillSubTab) {
      result = result.filter(m => m.module_type === skillSubTab);
    }
    // Filter by selected products
    if (skillFilterProducts.length > 0) {
      const linkedModuleIds = new Set();
      skillFilterProducts.forEach(pid => {
        moduleIdsForProduct(pid).forEach(mid => linkedModuleIds.add(mid));
      });
      result = result.filter(m => linkedModuleIds.has(m.id));
    }
    return result;
  }, [skillFilterProducts, metaModules, metaModuleProducts, skillSubTab]);

  // Filtered skill rows by sub-tab type
  const skillRowsForSubTab = useMemo(() => {
    let filtered = skillSubTab ? skillRows.filter(r => r.skill_type === skillSubTab) : [...skillRows];
    // Sort: Product(asc), Skill Group(asc), Self Score(desc), Skillset(asc)
    filtered.sort((a, b) => {
      const p = (a.product || '').localeCompare(b.product || '');
      if (p !== 0) return p;
      const m = (a.module || '').localeCompare(b.module || '');
      if (m !== 0) return m;
      // Self score descending — parse numeric or L-level for comparison
      const parseScore = (s) => { if (!s || s === '' || s === '—') return -1; const ls = String(s).match(/^L(\d+)$/i); if (ls) return parseInt(ls[1]); return parseFloat(s) || -1; };
      const sa = parseScore(a.self_score), sb = parseScore(b.self_score);
      if (sb !== sa) return sb - sa;
      return (a.skillset || '').localeCompare(b.skillset || '');
    });
    return filtered;
  }, [skillRows, skillSubTab]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (skillProdDropRef.current && !skillProdDropRef.current.contains(e.target)) setSkillProdDropOpen(false);
      if (skillModDropRef.current && !skillModDropRef.current.contains(e.target)) setSkillModDropOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Populate skills from metadata based on filters
  function populateSkillsFromMetadata() {
    // Get filtered module IDs
    let targetModuleIds = [];
    if (skillFilterModules.length > 0) {
      targetModuleIds = skillFilterModules;
    } else if (skillFilterProducts.length > 0) {
      const linkedModuleIds = new Set();
      skillFilterProducts.forEach(pid => {
        moduleIdsForProduct(pid).forEach(mid => linkedModuleIds.add(mid));
      });
      targetModuleIds = Array.from(linkedModuleIds);
    } else {
      targetModuleIds = metaModules.map(m => m.id);
    }

    // Filter by the active sub-tab type
    let filteredModules = metaModules.filter(m => targetModuleIds.includes(m.id));
    if (skillSubTab) {
      filteredModules = filteredModules.filter(m => m.module_type === skillSubTab);
    }
    const filteredModuleIds = new Set(filteredModules.map(m => m.id));

    // Get all skillsets for the filtered modules
    const matchingSkillsets = metaSkillsets.filter(s => filteredModuleIds.has(s.module_id));

    // Build new rows, preserving existing rows that aren't being replaced
    const existingKeys = new Set(skillRows.map(r => `${r.product}||${r.module}||${r.skillset}`));
    const newRows = [];

    matchingSkillsets.forEach(sk => {
      const mod = metaModules.find(m => m.id === sk.module_id);
      if (!mod) return;

      // Prefer the skillset's OWN product list (metadata_skillsets.product_ids).
      // Only fall back to all module products if the skillset has no specific products.
      // Accept both array (new /cascade response) and comma-separated string (legacy).
      const rawSkProductIds = sk.product_ids;
      const skProductIds = Array.isArray(rawSkProductIds)
        ? rawSkProductIds.filter(Boolean)
        : (rawSkProductIds ? String(rawSkProductIds).split(',').map(x => x.trim()).filter(Boolean) : []);
      const modProductIds = metaModuleProducts
        .filter(mp => mp.module_id === mod.id)
        .map(mp => mp.product_id);
      const assignableProductIds = skProductIds.length > 0 ? skProductIds : modProductIds;

      // If we have product filters, only use the selected ones; otherwise all
      const relevantProductIds = skillFilterProducts.length > 0
        ? assignableProductIds.filter(pid => skillFilterProducts.includes(pid))
        : assignableProductIds;

      relevantProductIds.forEach(pid => {
        const prod = metaProducts.find(p => p.id === pid);
        if (!prod) return;

        const key = `${prod.name}||${mod.name}||${sk.name}`;
        if (existingKeys.has(key)) return; // skip duplicates

        newRows.push({
          _key: `_new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          _dirty: true,
          skill_type: mod.module_type || 'Functional',
          product: prod.name,
          module: mod.name,
          skillset: sk.name,
          self_score: '',
          max_score: sk.max_score_scale || prod.max_score || 'L3',
        });
        existingKeys.add(key);
      });
    });

    if (newRows.length === 0) {
      setSubMsg('No new skills found matching the selected filters');
      return;
    }
    setSkillRows(prev => [...prev, ...newRows]);
    setSubMsg(`Added ${newRows.length} skill(s)`);
  }

  async function fetchSubTables(empId) {
    try {
      const [skRes, exRes, prRes] = await Promise.allSettled([
        apiFetch(`/api/employees/${empId}/employee_skills_v2`),
        apiFetch(`/api/employees/${empId}/employee_experience`),
        apiFetch(`/api/employees/${empId}/employee_projects`),
      ]);
      if (skRes.status === 'fulfilled' && skRes.value.ok) {
        const d = await skRes.value.json(); setSkillRows((d.rows || []).map(r => ({ ...r, _key: r.id })));
      } else { setSkillRows([]); }
      if (exRes.status === 'fulfilled' && exRes.value.ok) {
        const d = await exRes.value.json(); setExpRows((d.rows || []).map(r => ({ ...r, _key: r.id })));
      } else { setExpRows([]); }
      if (prRes.status === 'fulfilled' && prRes.value.ok) {
        const d = await prRes.value.json(); setProjRows((d.rows || []).map(r => ({ ...r, _key: r.id })));
      } else { setProjRows([]); }
    } catch (_) { /* handled */ }
  }

  async function openView(emp) {
    setDetailLoading(true);
    setMode('view');
    setActiveTab('personal');
    setErrors({});
    setSubMsg('');
    resetDirty();
    try {
      const res = await apiFetch(`/api/employees/${emp.id}`);
      if (res.ok) {
        const data = await res.json();
        const full = data.employee || data;
        setSelectedEmp(full);
        setForm(flattenEmployee(full));
        fetchSubTables(full.id);
      } else {
        setSelectedEmp(emp);
        setForm(flattenEmployee(emp));
        fetchSubTables(emp.id);
      }
    } catch (_) {
      setSelectedEmp(emp);
      setForm(flattenEmployee(emp));
      fetchSubTables(emp.id);
    }
    setDetailLoading(false);
  }

  function openEdit(emp) {
    if (selectedEmp && selectedEmp.id === (emp || selectedEmp).id) {
      setMode('edit');
    } else {
      openView(emp || selectedEmp).then(() => setMode('edit'));
      setMode('edit');
    }
  }

  function switchToEdit() {
    setMode('edit');
    resetDirty();
  }

  const [transferring, setTransferring] = useState(false);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [letterTemplates, setLetterTemplates] = useState([]);
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [letterLoading, setLetterLoading] = useState(false);
  const [generatingTemplate, setGeneratingTemplate] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', category: 'Appraisal Letter' });

  async function handleTransferInactive() {
    if (!(await toast.confirm({ body: 'This will move ALL employees with status other than "Active" to the employee_inactive table and remove them from the active employees list.\n\nAre you sure you want to proceed?', danger: true }))) return;
    setTransferring(true);
    try {
      const res = await apiFetch('/api/employees/transfer-inactive', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Transfer failed');
      toast.error(data.message || `${data.transferred} employee(s) transferred`)
      fetchEmployees();
    } catch (err) {
      toast.error('Error: ' + err.message)
    } finally {
      setTransferring(false);
    }
  }

  // ── HR Letter functions ──
  const LETTER_CATEGORIES = ['Appraisal Letter', 'Offer Letter', 'Experience Letter', 'Relieving Letter', 'Confirmation Letter', 'Warning Letter', 'Promotion Letter', 'Other'];

  async function openLetterModal() {
    setShowLetterModal(true);
    setLetterLoading(true);
    try {
      const [tplRes, genRes] = await Promise.all([
        apiFetch('/api/hr-letters'),
        selectedEmp ? apiFetch(`/api/hr-letters/generated/${selectedEmp.id}`) : Promise.resolve({ ok: true, json: () => ({ letters: [] }) }),
      ]);
      if (tplRes.ok) {
        const data = await tplRes.json();
        setLetterTemplates(data.templates || []);
      }
      if (genRes.ok) {
        const data = await genRes.json();
        setGeneratedLetters(data.letters || []);
      }
    } catch (err) { /* handled */ }
    setLetterLoading(false);
  }

  async function handleUploadTemplate(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTemplate(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', newTemplate.name || file.name.replace('.docx', ''));
      formData.append('category', newTemplate.category);
      const res = await apiFetch('/api/hr-letters/upload', { method: 'POST', body: formData, skipContentType: true });
      if (res.ok) {
        const data = await res.json();
        setLetterTemplates(prev => [data.template, ...prev]);
        setNewTemplate({ name: '', category: 'Appraisal Letter' });
        e.target.value = '';
      } else {
        const data = await res.json();
        toast.error(data.error || 'Upload failed')
      }
    } catch (err) { toast.error('Upload error') }
    setUploadingTemplate(false);
  }

  async function handleGenerateLetter(templateId) {
    if (!selectedEmp) return;
    setGeneratingTemplate(templateId);
    try {
      const res = await apiFetch(`/api/hr-letters/generate/${templateId}/${selectedEmp.id}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setGeneratedLetters(prev => [data.letter, ...prev]);
      } else {
        const data = await res.json();
        toast.error(data.error || 'Generation failed')
      }
    } catch (err) { toast.error('Generation error') }
    setGeneratingTemplate(null);
  }

  async function handleDownloadLetter(letterId) {
    try {
      const res = await apiFetch(`/api/hr-letters/download/${letterId}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const disposition = res.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?(.+?)"?$/);
        a.download = match ? match[1] : 'letter.docx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) { toast.error('Download error') }
  }

  async function handleEmailLetter(letterId) {
    const email = prompt('Enter recipient email address:', selectedEmp?.email || '');
    if (!email) return;
    try {
      const res = await apiFetch(`/api/hr-letters/email/${letterId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_to: email }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.error(data.message || 'Email sent')
        setGeneratedLetters(prev => prev.map(l => l.id === letterId ? { ...l, status: 'emailed', email_to: email } : l));
      } else {
        const data = await res.json();
        toast.error(data.error || 'Email failed')
      }
    } catch (err) { toast.error('Email error') }
  }

  async function handleDeleteTemplate(templateId) {
    if (!(await toast.confirm({ body: 'Delete this template?', danger: true }))) return;
    try {
      const res = await apiFetch(`/api/hr-letters/${templateId}`, { method: 'DELETE' });
      if (res.ok) setLetterTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (err) { toast.error('Delete error') }
  }

  function openCreate() {
    setSelectedEmp(null);
    setForm(buildEmptyForm());
    setErrors({});
    setActiveTab('personal');
    setMode('create');
    resetDirty();
  }

  function backToList() {
    if (!confirmIfDirty()) return;
    setMode('list');
    setSelectedEmp(null);
    setForm(buildEmptyForm());
    setErrors({});
    setSkillRows([]);
    setExpRows([]);
    setProjRows([]);
    setSubMsg('');
    setSkillFilterProducts([]);
    setSkillFilterModules([]);
  }

  /* ── Sub-table row helpers ── */
  let _keyCounter = 0;
  function newKey() { return `_new_${Date.now()}_${++_keyCounter}`; }

  function makeSubHandlers(rows, setRows) {
    return {
      onChange: (idx, key, value) => {
        setRows(prev => { const copy = [...prev]; copy[idx] = { ...copy[idx], [key]: value, _dirty: true }; return copy; });
        markDirty();
      },
      onAdd: () => {
        setRows(prev => [{ _key: newKey(), _dirty: true }, ...prev]);
        markDirty();
      },
      onDelete: (idx) => {
        setRows(prev => prev.filter((_, i) => i !== idx));
        markDirty();
      },
      onCopy: (idx) => {
        setRows(prev => {
          const src = { ...prev[idx] };
          delete src.id; // ensure new row
          src._key = newKey();
          src._dirty = true;
          const copy = [...prev];
          copy.splice(idx + 1, 0, src);
          return copy;
        });
      },
    };
  }

  const skillHandlers = makeSubHandlers(skillRows, setSkillRows);
  const expHandlers = makeSubHandlers(expRows, setExpRows);
  const projHandlers = makeSubHandlers(projRows, setProjRows);

  /* ── Check duplicates in sub-table ── */
  function markDuplicates(rows, keys) {
    const seen = {};
    return rows.map(r => {
      const k = keys.map(key => (r[key] || '').toString().toLowerCase()).join('||');
      if (k && seen[k]) {
        return { ...r, _duplicate: true };
      }
      if (k) seen[k] = true;
      return { ...r, _duplicate: false };
    });
  }

  async function saveSubTable(tableName, rows, setRows, empId) {
    setSubSaving(true);
    setSubMsg('');
    try {
      // Delete rows that existed but are now removed
      const existingIds = rows.filter(r => r.id).map(r => r.id);
      // Save all rows
      const res = await apiFetch(`/api/employees/${empId}/${tableName}`, {
        method: 'POST',
        body: JSON.stringify(rows),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.duplicates && data.duplicates.length > 0) {
          setSubMsg(`Saved with ${data.duplicates.length} duplicate(s) skipped`);
        } else {
          setSubMsg('Saved successfully');
        }
        // Refresh from server
        const refreshRes = await apiFetch(`/api/employees/${empId}/${tableName}`);
        if (refreshRes.ok) {
          const refreshData = await refreshRes.json();
          setRows((refreshData.rows || []).map(r => ({ ...r, _key: r.id })));
        }
      } else {
        const data = await res.json();
        setSubMsg(`Error: ${data.error || 'Failed to save'}`);
      }
    } catch (err) {
      setSubMsg('Network error while saving');
    }
    setSubSaving(false);
    setTimeout(() => setSubMsg(''), 4000);
  }

  function handleField(name, value) {
    setForm(prev => ({ ...prev, [name]: value }));
    markDirty();
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  }

  async function handleSave() {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) { setActiveTab('personal'); return; }

    setSaving(true);
    try {
      const isEdit = mode === 'edit' && selectedEmp;
      const url = isEdit ? `/api/employees/${selectedEmp.id}` : '/api/employees';
      const method = isEdit ? 'PUT' : 'POST';
      // Send computed fields (age, current_exp_nouveau) so the DB matches what the UI shows,
      // regardless of whatever value was in the input.
      const payload = { ...form };
      const computedAge = computeAge(form.date_of_birth);
      if (computedAge !== '') payload.age = computedAge;
      const computedExp = computeExperience(form.date_of_joining, form.exit_date);
      if (computedExp) payload.current_exp_nouveau = computedExp;
      const res = await apiFetch(url, { method, body: JSON.stringify(payload) });
      if (res.ok) {
        const data = await res.json();
        resetDirty();

        // Determine the saved employee id
        const savedId = isEdit ? selectedEmp.id : (data.employee ? data.employee.id : (data.id || null));

        // Refresh list, then go back to list with saved employee highlighted
        await fetchEmployees();
        setHighlightEmpId(savedId);
        setSelectedEmp(null);
        setForm(buildEmptyForm());
        setMode('list');

        // Scroll the saved employee row into view after render
        if (savedId) {
          setTimeout(() => {
            const row = document.querySelector(`[data-emp-id="${savedId}"]`);
            if (row) {
              row.scrollIntoView({ behavior: 'smooth', block: 'center' });
              row.classList.add('emp-row-flash');
              setTimeout(() => row.classList.remove('emp-row-flash'), 2000);
            }
          }, 150);
        }
      } else {
        const data = await res.json();
        setErrors({ _general: data.error || 'Failed to save employee' });
      }
    } catch (err) {
      setErrors({ _general: 'Network error' });
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/employees/${deleteConfirm.id}`, { method: 'DELETE' });
      if (res.ok) {
        setDeleteConfirm(null);
        fetchEmployees();
        backToList();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to delete employee')
      }
    } catch (err) {
      toast.error('Network error while deleting')
    }
    setDeleting(false);
  }

  /* ── Derive unique modules from data ── */
  const moduleOptions = [...new Set(employees.map(e => e.module).filter(Boolean))].sort();

  const filteredEmployees = employees.filter(emp => {
    const empStatus = (emp.status || 'Active').toUpperCase();
    // Tab-based status filtering
    if (statusTab === 'ACTIVE' && empStatus !== 'ACTIVE') return false;
    if (statusTab === 'RESIGNED' && empStatus !== 'RESIGNED') return false;
    if (statusTab === 'JOINING' && empStatus !== 'JOINING') return false;
    if (statusTab === 'NON_ACTIVE' && (empStatus === 'ACTIVE' || empStatus === 'RESIGNED' || empStatus === 'JOINING')) return false;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const name = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase();
      const code = (emp.emp_code || '').toLowerCase();
      const email = (emp.email || '').toLowerCase();
      const phone = (emp.phone || '').toLowerCase();
      if (!(name.includes(term) || code.includes(term) || email.includes(term) || phone.includes(term))) return false;
    }
    if (filterDept && String(emp.department_id) !== String(filterDept)) return false;
    if (filterDesig && String(emp.designation_id) !== String(filterDesig)) return false;
    if (filterModule && (emp.module || '') !== filterModule) return false;
    return true;
  });

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts = { ACTIVE: 0, RESIGNED: 0, JOINING: 0, NON_ACTIVE: 0 };
    employees.forEach(emp => {
      const s = (emp.status || 'Active').toUpperCase();
      if (s === 'ACTIVE') counts.ACTIVE++;
      else if (s === 'RESIGNED') counts.RESIGNED++;
      else if (s === 'JOINING') counts.JOINING++;
      else counts.NON_ACTIVE++;
    });
    return counts;
  }, [employees]);

  const activeFilterCount = [filterDept, filterDesig, filterModule].filter(Boolean).length;

  const columns = [
    { key: 'emp_code', label: 'Emp ID', sortable: true, width: '6%' },
    {
      key: '_avatar', label: '', sortable: false, width: 56,
      render: (_, row) => {
        const initialsBubble = (
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600, color: '#fff', flexShrink: 0,
            background: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
          }}>
            {getInitials(row.first_name, row.last_name)}
          </div>
        );
        if (!row.photo_path) return initialsBubble;
        return (
          <img
            src={row.photo_path}
            alt=""
            width="32"
            height="32"
            style={{
              width: 32, height: 32, minWidth: 32, minHeight: 32, maxWidth: 32, maxHeight: 32,
              borderRadius: '50%', objectFit: 'cover',
              border: '1px solid rgba(255,255,255,0.12)', display: 'block',
              flexShrink: 0,
            }}
            onError={(e) => {
              // Image failed to load — replace with initials bubble
              const parent = e.currentTarget.parentNode;
              if (parent) {
                parent.innerHTML = '';
                const div = document.createElement('div');
                div.style.cssText = 'width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:#fff;background:linear-gradient(135deg,#3B82F6 0%,#6366F1 100%);';
                div.textContent = (row.first_name?.[0] || '') + (row.last_name?.[0] || '') || '?';
                parent.appendChild(div);
              }
            }}
          />
        );
      },
    },
    {
      key: 'first_name', label: 'Name', sortable: true, width: '13%',
      render: (val, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '--'
    },
    { key: 'department_name', label: 'Dept', sortable: true, width: '10%' },
    { key: 'designation_name', label: 'Designation', sortable: true, width: '11%' },
    { key: 'module', label: 'Skill Group', sortable: true, width: '7%' },
    { key: 'email', label: 'Email', sortable: true, width: '17%' },
    { key: 'phone', label: 'Phone', sortable: true, width: '9%' },
    {
      key: 'status', label: 'Status', sortable: true, width: '7%',
      render: (val) => <span className={`badge ${STATUS_BADGES[val] || 'badge-gray'}`}>{val || 'Active'}</span>
    },
    {
      key: '_actions', label: '', sortable: false, width: '10%',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
          <button
            title="View"
            style={styles.actionBtn}
            onClick={() => openView(row)}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.15)'; e.currentTarget.style.color = '#60A5FA'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          ><IconView /></button>
          <button
            title="Edit"
            style={styles.actionBtn}
            onClick={() => { openView(row); setTimeout(() => setMode('edit'), 300); }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.15)'; e.currentTarget.style.color = '#60A5FA'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          ><IconEdit /></button>
          <button
            title="Delete"
            style={styles.actionBtnDanger}
            onClick={() => setDeleteConfirm(row)}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.2)'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.06)'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.2)'; }}
          ><IconDelete /></button>
        </div>
      )
    },
  ];

  const deptOptions = departments.map(d => ({ value: d.id, label: d.name }));
  const desigOptions = designations.map(d => ({ value: d.id, label: d.name }));
  const officeOptions = offices.map(o => ({ value: o.id, label: o.name }));
  const gradeOptions = grades.filter(g => g.status === 'Active').map(g => ({ value: g.id, label: g.name }));
  // Group Tag dropdown options — the same list shown on the Group Tag master.
  // The "▶" prefix on Monitor=true tags signals that membership turns on
  // the Workforce Live View tracking (Teams presence + sign-in city).
  const groupTagOptions = groupTags.map(t => ({
    value: t.id,
    label: (t.monitor ? '▶ ' : '') + t.name,
  }));
  const reportingOptions = employees
    .map(e => ({
      value: e.id,
      label: `${e.first_name || ''} ${e.last_name || ''}`.trim() + (e.emp_code ? ` (${e.emp_code})` : ''),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const age = computeAge(form.date_of_birth);
  const currentExp = computeExperience(form.date_of_joining, form.exit_date);
  const isEditable = mode === 'edit' || mode === 'create';

  /* ── Lookup helpers ── */
  function deptName(id) { const d = departments.find(x => String(x.id) === String(id)); return d ? d.name : id || '—'; }
  function desigName(id) { const d = designations.find(x => String(x.id) === String(id)); return d ? d.name : id || '—'; }
  function officeName(id) { const o = offices.find(x => String(x.id) === String(id)); return o ? o.name : id || '—'; }
  function gradeName(id) { const g = grades.find(x => String(x.id) === String(id)); return g ? g.name : id || '—'; }
  function empName(id) { const e = employees.find(x => String(x.id) === String(id)); return e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : id || '—'; }

  /* ── Tab Content Renderer ── */
  function renderTabContent() {
    if (isEditable) return renderEditableTab();
    return renderViewTab();
  }

  function renderViewTab() {
    switch (activeTab) {
      case 'personal':
        return (
          <div>
            <div className="form-row">
              <ViewField label="Emp ID" value={form.emp_code} />
              <ViewField label="First Name" value={form.first_name} />
              <ViewField label="Last Name" value={form.last_name} />
            </div>
            <div className="form-row">
              <ViewField label="Gender" value={form.gender} />
              <ViewField label="Date of Birth" value={form.date_of_birth} />
              <ViewField label="Age" value={age !== '' ? String(age) : ''} />
            </div>
            <div className="form-row">
              <ViewField label="Marital Status" value={form.marital_status} />
              <ViewField label="Nationality" value={form.nationality} />
            </div>
          </div>
        );
      case 'contact':
        return (
          <div>
            <div className="form-row">
              <ViewField label="Mobile No." value={form.phone} />
              <ViewField label="Alternate (Family) No." value={form.alternate_phone} />
            </div>
            <div className="form-row">
              <ViewField label="Email ID (Personal)" value={form.personal_email} />
              <ViewField label="Email ID (Official)" value={form.email} />
            </div>
          </div>
        );
      case 'employment':
        return (
          <div>
            <div className="form-row">
              <ViewField label="Department" value={deptName(form.department_id)} />
              <ViewField label="Skill Group" value={form.module} />
              <ViewField label="Designation" value={desigName(form.designation_id)} />
            </div>
            <div className="form-row">
              <ViewField label="Reporting Manager" value={empName(form.reporting_to)} />
              <ViewField label="Employee Type" value={form.employment_type} />
              <ViewField label="Employee Status" value={form.status} />
            </div>
            <div className="form-row">
              <ViewField label="Office" value={officeName(form.office_id)} />
              <ViewField label="Grade" value={gradeName(form.grade_id)} />
              <ViewField label="Group Tag" value={(groupTags.find(t => t.id === form.group_tag_id)?.name) || ''} />
            </div>
            <div className="form-row">
              <ViewField label="Date of Offer" value={form.date_of_offer} />
              <ViewField label="Expected Joining Date" value={form.expected_joining_date} />
              <ViewField label="Confirmed Joining Date" value={form.confirmed_joining_date} />
            </div>
            <div className="form-row">
              <ViewField label="Joining Status" value={form.joining_status} />
              <ViewField label="Probation End Date" value={form.probation_end_date} />
              <ViewField label="Probation Status" value={form.probation_status} />
            </div>
            <div className="form-row">
              <ViewField label="Current Exp - Nouveau" value={currentExp || form.current_exp_nouveau} />
              <ViewField label="Appraisal Cycle" value={form.appraisal_cycle} />
              <ViewField label="Location/Branch" value={form.location} />
            </div>
            <div className="form-row">
              <ViewField label="Home Location" value={form.home_location} />
            </div>
            <div className="form-row">
              <ViewField label="Current Address" value={form.current_address} />
            </div>
            <div className="form-row">
              <ViewField label="Offer Shared By" value={form.offer_shared_by} />
              <ViewField label="Approved By" value={form.approved_by} />
              <ViewField label="Final Approval" value={form.final_approval} />
            </div>
            <div className="form-row">
              <ViewField label="Exit Date" value={form.exit_date} />
              <ViewField label="Reason for Exit" value={form.reason_for_exit} />
              <ViewField label="Relieving Letter" value={form.relieving_letter} />
            </div>
          </div>
        );
      case 'compensation':
        return (
          <div>
            <div className="form-row">
              <ViewField label="Offer CTC (Annual)" value={form.offer_ctc_annual ? Number(form.offer_ctc_annual).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : ''} />
              <ViewField label="Revise Offer Date" value={form.revise_offer_date} />
              <ViewField label="Revise Offer Salary" value={form.revise_offer_salary ? Number(form.revise_offer_salary).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : ''} />
            </div>
            <div style={styles.sectionHeading}>Salary Breakup</div>
            <div className="form-row">
              <ViewField label="Gross PA (Fixed)" value={form.gross_pa_fixed} />
              <ViewField label="Gross PM (Fx+Var)" value={form.gross_pm_fx_var} />
              <ViewField label="Basic Salary" value={form.basic_salary} />
            </div>
            <div className="form-row">
              <ViewField label="HRA" value={form.hra} />
              <ViewField label="Statutory Bonus" value={form.statutory_bonus} />
              <ViewField label="LTA" value={form.lta} />
            </div>
            <div className="form-row">
              <ViewField label="Personal Allowance" value={form.personal_allowance} />
              <ViewField label="Variable Pay" value={form.variable_pay} />
              <ViewField label="PM" value={form.pm_amount} />
            </div>
            <div className="form-row">
              <ViewField label="Company PF" value={form.company_pf} />
              <ViewField label="Employee PF" value={form.employee_pf} />
              <ViewField label="PT" value={form.professional_tax} />
            </div>
            <div className="form-row">
              <ViewField label="Bonus" value={form.bonus} />
              <ViewField label="Net PM" value={form.net_pm} />
            </div>
            <div style={styles.sectionHeading}>Banking & PF</div>
            <div className="form-row">
              <ViewField label="Bank Name" value={form.bank_name} />
              <ViewField label="Bank Account Number" value={form.bank_account_number} />
              <ViewField label="IFSC Code" value={form.ifsc_code} />
            </div>
            <div className="form-row">
              <ViewField label="PF Holder" value={form.pf_holder} />
              <ViewField label="Interested for PF" value={form.interested_for_pf} />
              <ViewField label="PF Amt. Withdrawn" value={form.pf_amt_withdrawn} />
            </div>
            <div className="form-row">
              <ViewField label="PF Account No" value={form.pf_account_no} />
              <ViewField label="UAN Number" value={form.uan_number} />
              <ViewField label="Old UAN Number" value={form.old_uan_number} />
            </div>
            <div className="form-row">
              <ViewField label="PF Wages (P.M)" value={form.pf_wages_pm} />
              <ViewField label="PF Date Joining" value={form.pf_date_joining} />
            </div>
            <div style={styles.sectionHeading}>Tax & ID</div>
            <div className="form-row">
              <ViewField label="PAN Number" value={form.pan_number} />
              <ViewField label="Aadhaar Number" value={form.aadhaar_number} />
              <ViewField label="ESIC Number" value={form.esic_number} />
            </div>
          </div>
        );
      case 'documents':
        return (
          <div>
            <div className="form-row">
              <ViewField label="PAN Submitted" value={form.pan_submitted} />
              <ViewField label="Aadhaar Submitted" value={form.aadhaar_submitted} />
              <ViewField label="Resume Submitted" value={form.resume_submitted} />
            </div>
            <div className="form-row">
              <ViewField label="Educational Certificates" value={form.edu_certificates_submitted} />
              <ViewField label="Experience Letters" value={form.experience_letters_submitted} />
              <ViewField label="Bank Statement" value={form.bank_statement_submitted} />
            </div>
            <div className="form-row">
              <ViewField label="Documents Completed" value={form.documents_completed} />
              <ViewField label="Offer Letter Issued" value={form.offer_letter_issued} />
              <ViewField label="Appointment Letter Issued" value={form.appointment_letter_issued} />
            </div>
            <div className="form-row">
              <ViewField label="Bond" value={form.bond} />
              <ViewField label="Bond Duration" value={form.bond_duration} />
              <ViewField label="Bond Signed" value={form.bond_sign} />
            </div>
          </div>
        );
      case 'certification':
        return (
          <div>
            <div className="form-row">
              <ViewField label="SAP Certification" value={form.sap_certification} />
              <ViewField label="Certificate Validity" value={form.certificate_validity} />
            </div>
            <div className="form-row">
              <ViewField label="Credly Link" value={form.credly_link} />
            </div>
          </div>
        );
      case 'skills':
        return (
          <div>
            {/* ── Skill Sub-Tabs (view mode) ── */}
            <div className="skill-subtabs">
              {['Functional', 'Technical', 'Administration', 'Others'].map(t => (
                <button key={t} className={`skill-subtab${skillSubTab === t ? ' active' : ''}`}
                  onClick={() => { setSkillSubTab(t); setSkillFilterModules([]); }}>
                  {t}
                  <span className="skill-subtab-count">{skillRows.filter(r => r.skill_type === t).length}</span>
                </button>
              ))}
            </div>
            {skillRowsForSubTab.length > 0 ? (
              <InlineTable columns={SKILL_COLUMNS} rows={markDuplicates(skillRowsForSubTab, ['skillset','product','module'])} readOnly={true} tableName="skills" />
            ) : (
              <div style={{ padding: 24, textAlign: 'center', color: '#64748B' }}>No {skillSubTab.toLowerCase()} skills assigned yet.</div>
            )}
          </div>
        );
      case 'experience':
        return (
          <div>
            <InlineTable columns={EXPERIENCE_COLUMNS} rows={markDuplicates(expRows, ['organization','from_date'])} readOnly={true} tableName="experience" />
          </div>
        );
      case 'projects':
        return (
          <div>
            <InlineTable columns={PROJECT_COLUMNS} rows={markDuplicates(projRows, ['customer','from_date'])} readOnly={true} tableName="projects" />
          </div>
        );
      case 'transit':
        return (
          <div>
            <div className="form-row">
              <ViewField label="Metropolis" value={form.transit_metropolis} />
              <ViewField label="Means" value={form.transit_means} />
              <ViewField label="Line" value={form.transit_line} />
            </div>
            <div className="form-row">
              <ViewField label="Zone" value={form.transit_zone} />
              <ViewField label="Locality" value={form.transit_locality} />
              <ViewField label="Duration" value={form.transit_duration} />
            </div>
            <div className="form-row">
              <ViewField label="Local Address" value={form.local_address} />
            </div>
          </div>
        );
      case 'assets':
        return (
          <div>
            <div className="form-row">
              <ViewField label="Blood Group" value={form.blood_group} />
              <ViewField label="Laptop/Charger" value={form.laptop_charger} />
              <ViewField label="ID Card" value={form.id_card} />
            </div>
            <div className="form-row">
              <ViewField label="Diary" value={form.diary} />
              <ViewField label="Pen" value={form.pen} />
              <ViewField label="Bag" value={form.bag} />
            </div>
            <div className="form-row">
              <ViewField label="T-Shirt" value={form.tshirt} />
              <ViewField label="T-Shirt Size" value={form.tshirt_size} />
              <ViewField label="LAN Wire" value={form.lan_wire} />
            </div>
            <div className="form-row">
              <ViewField label="Car/Bike" value={form.car_bike} />
              <ViewField label="Vehicle No." value={form.vehicle_no} />
              <ViewField label="Tag ID" value={form.tag_id} />
            </div>
          </div>
        );
      default: return null;
    }
  }

  function renderEditableTab() {
    switch (activeTab) {
      case 'personal':
        return (
          <div>
            <div className="form-row">
              <FormField label="Emp ID" name="emp_code" value={form.emp_code} onChange={handleField}
                disabled={mode === 'edit' && !isAdmin}
                hint={mode === 'edit' && !isAdmin ? 'Only Admin can change Emp ID after creation' : undefined} />
              <FormField label="Title" name="title" type="select" value={form.title} onChange={handleField}
                options={['Mr', 'Ms', 'Mrs', 'Doctor']} placeholder="Title" />
              <FormField label="First Name" name="first_name" value={form.first_name} onChange={handleField}
                disabled={mode === 'edit' && !isAdmin}
                error={errors.first_name} required
                hint={mode === 'edit' && !isAdmin ? 'Only Admin can change Name after creation' : undefined} />
              <FormField label="Last Name" name="last_name" value={form.last_name} onChange={handleField}
                disabled={mode === 'edit' && !isAdmin}
                error={errors.last_name} required />
            </div>
            <div className="form-row">
              <FormField label="Gender" name="gender" type="select" value={form.gender} onChange={handleField}
                options={GENDER_OPTIONS} />
              <FormField label="Date of Birth" name="date_of_birth" type="date" value={form.date_of_birth}
                onChange={handleField} />
              <FormField label="Age" name="age" value={age !== '' ? String(age) : ''} onChange={() => {}} disabled />
            </div>
            <div className="form-row">
              <FormField label="Marital Status" name="marital_status" type="select" value={form.marital_status}
                onChange={handleField} options={MARITAL_OPTIONS} />
              <FormField label="Nationality" name="nationality" value={form.nationality} onChange={handleField} />
            </div>
          </div>
        );
      case 'contact':
        return (
          <div>
            <div className="form-row">
              <FormField label="Mobile No." name="phone" value={form.phone} onChange={handleField} />
              <FormField label="Alternate (Family) No." name="alternate_phone" value={form.alternate_phone}
                onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="Email ID (Personal)" name="personal_email" type="email" value={form.personal_email}
                onChange={handleField} />
              <FormField label="Email ID (Official)" name="email" type="email" value={form.email}
                onChange={handleField} />
            </div>
          </div>
        );
      case 'employment':
        return (
          <div>
            <div className="form-row">
              <FormField label="Department" name="department_id" type="select" value={form.department_id}
                onChange={handleField} options={deptOptions} />
              <FormField label="Skill Group" name="module" value={form.module} onChange={handleField} />
              <FormField label="Designation" name="designation_id" type="select" value={form.designation_id}
                onChange={handleField} options={desigOptions} />
            </div>
            <div className="form-row">
              <FormField label="Reporting Manager" name="reporting_to" type="employee-search" value={form.reporting_to}
                onChange={handleField} options={reportingOptions} placeholder="Search Manager..." />
              <FormField label="Employee Type" name="employment_type" type="select" value={form.employment_type}
                onChange={handleField} options={EMP_TYPE_OPTIONS} />
              <FormField label="Employee Status" name="status" type="select" value={form.status}
                onChange={handleField} options={STATUS_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Office" name="office_id" type="select" value={form.office_id}
                onChange={handleField} options={officeOptions} placeholder="Select Office" />
              <FormField label="Grade" name="grade_id" type="select" value={form.grade_id}
                onChange={handleField} options={gradeOptions} placeholder="Select Grade" />
              {/* Group Tag — moved here from the IT/vehicle section. The "▶"
                  prefix on Monitor=true tags signals that membership turns on
                  the Workforce Live View tracking. */}
              <FormField label="Group Tag" name="group_tag_id" type="select"
                value={form.group_tag_id} onChange={handleField}
                options={groupTagOptions} placeholder="Select Group Tag" />
            </div>
            <div className="form-row">
              <FormField label="Date of Offer" name="date_of_offer" type="date" value={form.date_of_offer}
                onChange={handleField} />
              {/* Expected vs Confirmed joining dates — Expected is what we
                  promised at offer time and surfaces on the Org Tree while the
                  candidate is in 'Offered' state; Confirmed is the firm date
                  once they've accepted and turn into 'Joining'. */}
              <FormField label="Expected Joining Date" name="expected_joining_date" type="date"
                value={form.expected_joining_date} onChange={handleField} />
              <FormField label="Confirmed Joining Date" name="confirmed_joining_date" type="date"
                value={form.confirmed_joining_date} onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="Joining Status" name="joining_status" value={form.joining_status}
                onChange={handleField} />
              <FormField label="Probation End Date" name="probation_end_date" type="date"
                value={form.probation_end_date} onChange={handleField} />
              <FormField label="Probation Status" name="probation_status" type="select"
                value={form.probation_status} onChange={handleField} options={PROBATION_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Current Exp - Nouveau" name="current_exp_nouveau"
                value={currentExp || form.current_exp_nouveau} onChange={() => {}} disabled
              />
              <FormField label="Appraisal Cycle" name="appraisal_cycle" value={form.appraisal_cycle}
                onChange={handleField} />
              <FormField label="Location/Branch" name="location" value={form.location} onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="Home Location" name="home_location" value={form.home_location}
                onChange={handleField} />
              <RegionPicker label="Region" value={form.region_id}
                onChange={(v) => handleField('region_id', v)} placeholder="Select region..." />
            </div>
            <div className="form-row">
              <FormField label="Current Address" name="current_address" type="textarea" value={form.current_address}
                onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="Offer Shared By" name="offer_shared_by" value={form.offer_shared_by}
                onChange={handleField} />
              <FormField label="Approved By" name="approved_by" value={form.approved_by} onChange={handleField} />
              <FormField label="Final Approval" name="final_approval" value={form.final_approval}
                onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="Exit Date" name="exit_date" type="date" value={form.exit_date}
                onChange={handleField} />
              <FormField label="Reason for Exit" name="reason_for_exit" value={form.reason_for_exit}
                onChange={handleField} />
              <FormField label="Relieving Letter" name="relieving_letter" value={form.relieving_letter}
                onChange={handleField} />
            </div>
          </div>
        );
      case 'compensation':
        return (
          <div>
            {/* Salary Schema picker — sits at the top so the rest of the
                tab is read in the context of "rates resolved against this
                schema". When set, payroll runs and the resolver use it. */}
            <div className="form-row">
              <FormField label="Salary Schema" name="salary_schema_id" type="select"
                value={form.salary_schema_id} onChange={handleField}
                options={[
                  { value: '', label: '— None —' },
                  ...salarySchemas.filter(s => s.status === 'Active').map(s => ({ value: s.id, label: `${s.code} · ${s.name}` })),
                ]}
                placeholder={salarySchemas.length === 0 ? 'No salary schemas defined yet' : 'Select salary schema...'} />
              <div></div>
              <div></div>
            </div>
            <div className="form-row">
              {/* Currency inputs use type="currency" so the displayed value
                  carries Indian thousand-separators (8,00,000) while state
                  stores the raw numeric string. */}
              <FormField label="Offer CTC (Annual)" name="offer_ctc_annual" type="currency"
                value={form.offer_ctc_annual} onChange={handleField} />
              <FormField label="Revise Offer Date" name="revise_offer_date" type="date"
                value={form.revise_offer_date} onChange={handleField} />
              <FormField label="Revise Offer Salary" name="revise_offer_salary" type="currency"
                value={form.revise_offer_salary} onChange={handleField} />
            </div>
            {/* Salary Breakup is now schema-driven — replaces the previous
                static Gross/Basic/HRA fields. The CompensationSchemaPanel
                pulls the picked Salary Schema's components, calls the
                resolver to surface master rates per component, lets HR
                override per-component monthly values, and saves a snapshot
                row to employee_salary_structures with validity dates. */}
            <div style={styles.sectionHeading}>Salary Breakup</div>
            <CompensationSchemaPanel
              employeeId={selectedEmp?.id || form.id}
              schemaId={form.salary_schema_id}
              ctcAnnual={form.offer_ctc_annual}
              currency="INR"
            />
            <div style={styles.sectionHeading}>Banking & PF</div>
            <div className="form-row">
              <FormField label="Bank Name" name="bank_name" value={form.bank_name} onChange={handleField} />
              <FormField label="Bank Account Number" name="bank_account_number" value={form.bank_account_number}
                onChange={handleField} />
              <FormField label="IFSC Code" name="ifsc_code" value={form.ifsc_code} onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="PF Holder" name="pf_holder" value={form.pf_holder} onChange={handleField} />
              <FormField label="Interested for PF" name="interested_for_pf" type="select"
                value={form.interested_for_pf} onChange={handleField} options={PF_INTEREST_OPTIONS} />
              <FormField label="PF Amt. Withdrawn" name="pf_amt_withdrawn" type="number"
                value={form.pf_amt_withdrawn} onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="PF Account No" name="pf_account_no" value={form.pf_account_no}
                onChange={handleField} />
              <FormField label="UAN Number" name="uan_number" value={form.uan_number} onChange={handleField} />
              <FormField label="Old UAN Number" name="old_uan_number" value={form.old_uan_number}
                onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="PF Wages (P.M)" name="pf_wages_pm" type="number" value={form.pf_wages_pm}
                onChange={handleField} />
              <FormField label="PF Date Joining" name="pf_date_joining" type="date" value={form.pf_date_joining}
                onChange={handleField} />
            </div>
            <div style={styles.sectionHeading}>Tax & ID</div>
            <div className="form-row">
              <FormField label="PAN Number" name="pan_number" value={form.pan_number} onChange={handleField} />
              <FormField label="Aadhaar Number" name="aadhaar_number" value={form.aadhaar_number}
                onChange={handleField} />
              <FormField label="ESIC Number" name="esic_number" value={form.esic_number} onChange={handleField} />
            </div>
          </div>
        );
      case 'documents':
        return (
          <div>
            <div className="form-row">
              <FormField label="PAN Submitted" name="pan_submitted" type="select" value={form.pan_submitted}
                onChange={handleField} options={DOC_STATUS_OPTIONS} />
              <FormField label="Aadhaar Submitted" name="aadhaar_submitted" type="select"
                value={form.aadhaar_submitted} onChange={handleField} options={DOC_STATUS_OPTIONS} />
              <FormField label="Resume Submitted" name="resume_submitted" type="select"
                value={form.resume_submitted} onChange={handleField} options={DOC_STATUS_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Educational Certificates" name="edu_certificates_submitted" type="select"
                value={form.edu_certificates_submitted} onChange={handleField} options={DOC_STATUS_OPTIONS} />
              <FormField label="Experience Letters" name="experience_letters_submitted" type="select"
                value={form.experience_letters_submitted} onChange={handleField} options={DOC_STATUS_OPTIONS} />
              <FormField label="Bank Statement" name="bank_statement_submitted" type="select"
                value={form.bank_statement_submitted} onChange={handleField} options={DOC_STATUS_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Documents Completed" name="documents_completed" type="select"
                value={form.documents_completed} onChange={handleField} options={DOC_STATUS_OPTIONS} />
              <FormField label="Offer Letter Issued" name="offer_letter_issued" type="select"
                value={form.offer_letter_issued} onChange={handleField} options={DOC_STATUS_OPTIONS} />
              <FormField label="Appointment Letter Issued" name="appointment_letter_issued" type="select"
                value={form.appointment_letter_issued} onChange={handleField} options={DOC_STATUS_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Bond" name="bond" type="select" value={form.bond} onChange={handleField}
                options={DOC_STATUS_OPTIONS} />
              <FormField label="Bond Duration" name="bond_duration" type="select" value={form.bond_duration}
                onChange={handleField} options={DOC_STATUS_OPTIONS} />
              <FormField label="Bond Signed" name="bond_sign" type="select" value={form.bond_sign}
                onChange={handleField} options={DOC_STATUS_OPTIONS} />
            </div>
          </div>
        );
      case 'certification':
        return (
          <div>
            <div className="form-row">
              <FormField label="SAP Certification" name="sap_certification" value={form.sap_certification}
                onChange={handleField} />
              <FormField label="Certificate Validity" name="certificate_validity" value={form.certificate_validity}
                onChange={handleField} />
            </div>
            <div className="form-row">
              <FormField label="Credly Link" name="credly_link" value={form.credly_link} onChange={handleField} />
            </div>
          </div>
        );
      case 'skills':
        return (
          <div>
            {/* ── Skill Sub-Tabs ── */}
            <div className="skill-subtabs">
              {['Functional', 'Technical', 'Administration', 'Others'].map(t => (
                <button key={t} className={`skill-subtab${skillSubTab === t ? ' active' : ''}`}
                  onClick={() => { setSkillSubTab(t); setSkillFilterModules([]); }}>
                  {t}
                  <span className="skill-subtab-count">{skillRows.filter(r => r.skill_type === t).length}</span>
                </button>
              ))}
            </div>

            {/* ── Filter bar: Products dropdown + Modules dropdown + Populate button ── */}
            <div className="skill-filter-bar">
              {/* Products multi-select dropdown */}
              <div className="skill-dropdown-wrap" ref={skillProdDropRef}>
                <button className="skill-dropdown-trigger" onClick={() => { setSkillProdDropOpen(!skillProdDropOpen); setSkillModDropOpen(false); }}>
                  <span>{skillFilterProducts.length === 0 ? 'All Products' : `${skillFilterProducts.length} Product${skillFilterProducts.length > 1 ? 's' : ''}`}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {skillProdDropOpen && (
                  <div className="skill-dropdown-menu">
                    {metaProducts.length === 0 && <div className="skill-dropdown-empty">No products configured</div>}
                    {metaProducts.map(p => {
                      const sel = skillFilterProducts.includes(p.id);
                      return (
                        <label key={p.id} className={`skill-dropdown-item${sel ? ' checked' : ''}`}>
                          <input type="checkbox" checked={sel} onChange={() => {
                            const next = sel ? skillFilterProducts.filter(x => x !== p.id) : [...skillFilterProducts, p.id];
                            setSkillFilterProducts(next);
                            if (next.length > 0) {
                              const validModIds = new Set();
                              next.forEach(pid => moduleIdsForProduct(pid).forEach(mid => validModIds.add(mid)));
                              setSkillFilterModules(prev => prev.filter(mid => validModIds.has(mid)));
                            }
                          }} />
                          {p.name}
                        </label>
                      );
                    })}
                    {skillFilterProducts.length > 0 && (
                      <button className="skill-dropdown-clear" onClick={() => { setSkillFilterProducts([]); setSkillFilterModules([]); }}>Clear All</button>
                    )}
                  </div>
                )}
              </div>

              {/* Modules multi-select dropdown */}
              <div className="skill-dropdown-wrap" ref={skillModDropRef}>
                <button className="skill-dropdown-trigger" onClick={() => { setSkillModDropOpen(!skillModDropOpen); setSkillProdDropOpen(false); }}>
                  <span>{skillFilterModules.length === 0 ? 'All Skill Groups' : `${skillFilterModules.length} Skill Group${skillFilterModules.length > 1 ? 's' : ''}`}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {skillModDropOpen && (
                  <div className="skill-dropdown-menu">
                    {skillAvailableModules.length === 0 && <div className="skill-dropdown-empty">Select products first</div>}
                    {skillAvailableModules.map(m => {
                      const sel = skillFilterModules.includes(m.id);
                      return (
                        <label key={m.id} className={`skill-dropdown-item${sel ? ' checked' : ''}`}>
                          <input type="checkbox" checked={sel} onChange={() => {
                            setSkillFilterModules(prev => sel ? prev.filter(x => x !== m.id) : [...prev, m.id]);
                          }} />
                          {m.name}
                        </label>
                      );
                    })}
                    {skillFilterModules.length > 0 && (
                      <button className="skill-dropdown-clear" onClick={() => setSkillFilterModules([])}>Clear All</button>
                    )}
                  </div>
                )}
              </div>

              <button className="meta-btn meta-btn-primary" onClick={populateSkillsFromMetadata}
                disabled={metaProducts.length === 0} style={{ padding: '5px 14px', fontSize: 12 }}>
                Populate Skills
              </button>
            </div>

            {/* ── Skills Table (filtered by sub-tab) ── */}
            <InlineTable columns={SKILL_COLUMNS} rows={markDuplicates(skillRowsForSubTab, ['skillset','product','module'])}
              onChange={(idx, key, value) => {
                // Map filtered index back to real skillRows index
                const realRow = skillRowsForSubTab[idx];
                const realIdx = skillRows.indexOf(realRow);
                if (realIdx >= 0) skillHandlers.onChange(realIdx, key, value);
              }}
              onAdd={skillHandlers.onAdd} onDelete={(idx) => {
                const realRow = skillRowsForSubTab[idx];
                const realIdx = skillRows.indexOf(realRow);
                if (realIdx >= 0) skillHandlers.onDelete(realIdx);
              }}
              onCopy={(idx) => {
                const realRow = skillRowsForSubTab[idx];
                const realIdx = skillRows.indexOf(realRow);
                if (realIdx >= 0) skillHandlers.onCopy(realIdx);
              }} tableName="skills" />
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="meta-btn meta-btn-primary"
                onClick={() => saveSubTable('employee_skills_v2', skillRows, setSkillRows, selectedEmp.id)}
                disabled={subSaving}>{subSaving ? 'Saving...' : 'Save Skills'}</button>
              <button className="meta-btn meta-btn-outline"
                onClick={() => { setSkillRows([]); setSubMsg('Skills cleared'); }}>Clear All</button>
              {subMsg && <span style={{ fontSize: 12, color: subMsg.startsWith('Error') ? '#F87171' : '#34D399' }}>{subMsg}</span>}
            </div>
          </div>
        );
      case 'experience':
        return (
          <div>
            <InlineTable columns={EXPERIENCE_COLUMNS} rows={markDuplicates(expRows, ['organization','from_date'])}
              onChange={expHandlers.onChange} onAdd={expHandlers.onAdd} onDelete={expHandlers.onDelete}
              onCopy={expHandlers.onCopy} tableName="experience" />
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 16px' }}
                onClick={() => saveSubTable('employee_experience', expRows, setExpRows, selectedEmp.id)}
                disabled={subSaving}>{subSaving ? 'Saving...' : 'Save Experience'}</button>
              {subMsg && <span style={{ fontSize: 12, color: subMsg.startsWith('Error') ? '#F87171' : '#34D399' }}>{subMsg}</span>}
            </div>
          </div>
        );
      case 'projects':
        return (
          <div>
            <InlineTable columns={PROJECT_COLUMNS} rows={markDuplicates(projRows, ['customer','from_date'])}
              onChange={projHandlers.onChange} onAdd={projHandlers.onAdd} onDelete={projHandlers.onDelete}
              onCopy={projHandlers.onCopy} tableName="projects" />
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 16px' }}
                onClick={() => saveSubTable('employee_projects', projRows, setProjRows, selectedEmp.id)}
                disabled={subSaving}>{subSaving ? 'Saving...' : 'Save Projects'}</button>
              {subMsg && <span style={{ fontSize: 12, color: subMsg.startsWith('Error') ? '#F87171' : '#34D399' }}>{subMsg}</span>}
            </div>
          </div>
        );
      case 'transit': {
        // Cascading filter logic from transit master data
        const td = transitData.filter(t => t.status === 'Active' || !t.status);
        const metropolisOpts = [...new Set(td.map(t => t.metropolis).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
        const meansFiltered = td.filter(t => !form.transit_metropolis || t.metropolis === form.transit_metropolis);
        const meansOpts = [...new Set(meansFiltered.map(t => t.means).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
        const lineFiltered = meansFiltered.filter(t => !form.transit_means || t.means === form.transit_means);
        const lineOpts = [...new Set(lineFiltered.map(t => t.line).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
        const zoneFiltered = lineFiltered.filter(t => !form.transit_line || t.line === form.transit_line);
        const zoneOpts = [...new Set(zoneFiltered.map(t => t.zone).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
        const localityFiltered = zoneFiltered.filter(t => !form.transit_zone || t.zone === form.transit_zone);
        const localityOpts = [...new Set(localityFiltered.map(t => t.locality).filter(Boolean))].sort().map(v => ({ value: v, label: v }));

        // Handler to cascade clear child fields and auto-fill duration
        const handleTransitField = (n, v) => {
          const updates = { [n]: v };
          if (n === 'transit_metropolis') { updates.transit_means = ''; updates.transit_line = ''; updates.transit_zone = ''; updates.transit_locality = ''; updates.transit_duration = ''; }
          else if (n === 'transit_means') { updates.transit_line = ''; updates.transit_zone = ''; updates.transit_locality = ''; updates.transit_duration = ''; }
          else if (n === 'transit_line') { updates.transit_zone = ''; updates.transit_locality = ''; updates.transit_duration = ''; }
          else if (n === 'transit_zone') { updates.transit_locality = ''; updates.transit_duration = ''; }
          else if (n === 'transit_locality') {
            // Auto-fill duration from matching transit record
            const match = td.find(t =>
              t.metropolis === (updates.transit_metropolis || form.transit_metropolis) &&
              t.means === (updates.transit_means || form.transit_means) &&
              t.locality === v
            );
            updates.transit_duration = match?.duration || '';
          }
          setForm(prev => ({ ...prev, ...updates }));
          markDirty();
        };

        return (
          <div>
            <div className="form-row">
              <FormField label="Metropolis" name="transit_metropolis" type="select" value={form.transit_metropolis}
                onChange={handleTransitField} options={metropolisOpts} placeholder="Select Metropolis" />
              <FormField label="Means" name="transit_means" type="select" value={form.transit_means}
                onChange={handleTransitField} options={meansOpts} placeholder="Select Means" />
              <FormField label="Line" name="transit_line" type="select" value={form.transit_line}
                onChange={handleTransitField} options={lineOpts} placeholder="Select Line" />
            </div>
            <div className="form-row">
              <FormField label="Zone" name="transit_zone" type="select" value={form.transit_zone}
                onChange={handleTransitField} options={zoneOpts} placeholder="Select Zone" />
              <FormField label="Locality" name="transit_locality" type="select" value={form.transit_locality}
                onChange={handleTransitField} options={localityOpts} placeholder="Select Locality" />
              <FormField label="Duration" name="transit_duration" value={form.transit_duration}
                onChange={handleField} placeholder="e.g. 45 mins" />
            </div>
            <FormField label="Local Address" name="local_address" type="textarea" rows={3}
              value={form.local_address} onChange={handleField}
              placeholder="Enter local residential address..." />
          </div>
        );
      }
      case 'assets':
        return (
          <div>
            <div className="form-row">
              <FormField label="Blood Group" name="blood_group" type="select" value={form.blood_group}
                onChange={handleField} options={BLOOD_GROUP_OPTIONS} />
              <FormField label="Laptop/Charger" name="laptop_charger" value={form.laptop_charger}
                onChange={handleField} />
              <FormField label="ID Card" name="id_card" type="select" value={form.id_card}
                onChange={handleField} options={YES_NO_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Diary" name="diary" type="select" value={form.diary} onChange={handleField}
                options={YES_NO_OPTIONS} />
              <FormField label="Pen" name="pen" type="select" value={form.pen} onChange={handleField}
                options={YES_NO_OPTIONS} />
              <FormField label="Bag" name="bag" type="select" value={form.bag} onChange={handleField}
                options={YES_NO_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="T-Shirt" name="tshirt" type="select" value={form.tshirt} onChange={handleField}
                options={YES_NO_OPTIONS} />
              <FormField label="T-Shirt Size" name="tshirt_size" type="select" value={form.tshirt_size}
                onChange={handleField} options={TSHIRT_SIZE_OPTIONS} />
              <FormField label="LAN Wire" name="lan_wire" type="select" value={form.lan_wire}
                onChange={handleField} options={YES_NO_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Car/Bike" name="car_bike" value={form.car_bike} onChange={handleField} />
              <FormField label="Vehicle No." name="vehicle_no" value={form.vehicle_no} onChange={handleField} />
              <FormField label="Tag ID" name="tag_id" value={form.tag_id} onChange={handleField} />
            </div>
          </div>
        );
      default: return null;
    }
  }

  /* ── Initials for avatar ── */
  function getInitials(f, l) {
    return ((f || '')[0] || '') + ((l || '')[0] || '') || '?';
  }

  /* ── Clear all filters ── */
  function clearFilters() {
    setSearchTerm('');
    setFilterDept('');
    setFilterDesig('');
    setFilterModule('');
  }

  /* ── LIST VIEW ── */
  if (mode === 'list') {
    return (
      <div>
        {/* Top row: search + new button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12,
          gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="form-input"
                style={{ width: 280, paddingLeft: 34 }}
                placeholder="Search by name, ID, email, phone..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconPlus /> New Employee
          </button>
        </div>

        {/* Status tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {[
            { key: 'ACTIVE', label: 'Active', color: '#22C55E' },
            { key: 'RESIGNED', label: 'Resigned', color: '#F59E0B' },
            { key: 'JOINING', label: 'Joining', color: '#3B82F6' },
            { key: 'NON_ACTIVE', label: 'Non Active', color: '#94A3B8' },
          ].map(tab => {
            const isActive = statusTab === tab.key;
            const count = tabCounts[tab.key];
            return (
              <button
                key={tab.key}
                onClick={() => setStatusTab(tab.key)}
                style={{
                  padding: '10px 20px', fontSize: 13, fontWeight: isActive ? 600 : 400,
                  color: isActive ? tab.color : '#64748B',
                  background: isActive ? `${tab.color}10` : 'transparent',
                  border: 'none', borderBottom: isActive ? `2px solid ${tab.color}` : '2px solid transparent',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all 0.15s ease',
                }}
              >
                {tab.label}
                <span style={{
                  fontSize: 11, padding: '1px 7px', borderRadius: 10,
                  background: isActive ? `${tab.color}20` : 'rgba(255,255,255,0.05)',
                  color: isActive ? tab.color : '#64748B',
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap',
          padding: '12px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          <select className="form-input" style={{ width: 150, fontSize: 12.5 }} value={filterDept}
            onChange={e => setFilterDept(e.target.value)}>
            <option value="">All Departments</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select className="form-input" style={{ width: 150, fontSize: 12.5 }} value={filterDesig}
            onChange={e => setFilterDesig(e.target.value)}>
            <option value="">All Designations</option>
            {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select className="form-input" style={{ width: 140, fontSize: 12.5 }} value={filterModule}
            onChange={e => setFilterModule(e.target.value)}>
            <option value="">All Skill Groups</option>
            {moduleOptions.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px',
                fontSize: 12, color: '#F87171', background: 'rgba(220,38,38,0.08)',
                border: '1px solid rgba(220,38,38,0.2)', borderRadius: 6, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Clear ({activeFilterCount})
            </button>
          )}
          <span style={{ fontSize: 12, color: '#64748B', marginLeft: 'auto' }}>
            {filteredEmployees.length} of {employees.length} employees
          </span>
        </div>

        <DataTable
          columns={columns}
          data={filteredEmployees}
          loading={loading}
          emptyMessage="No employees found. Add your first employee to get started."
          emptyIcon={'\u{1F464}'}
          onRowClick={(row) => openView(row)}
          fixedLayout
        />

        {/* Delete confirmation overlay */}
        {deleteConfirm && (
          <div style={styles.deleteConfirm} onClick={() => !deleting && setDeleteConfirm(null)}>
            <div style={styles.deleteBox} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#F1F5F9' }}>Delete Employee</h3>
              <p style={{ margin: '0 0 24px', fontSize: 14, color: '#94A3B8', lineHeight: 1.5 }}>
                Are you sure you want to delete <strong style={{ color: '#E2E8F0' }}>
                {deleteConfirm.first_name} {deleteConfirm.last_name}</strong> ({deleteConfirm.emp_code || 'No ID'})?
                This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                  Cancel
                </button>
                <button
                  className="btn"
                  style={{ background: 'rgba(220,38,38,0.8)', color: '#fff', border: 'none', padding: '8px 20px',
                    borderRadius: 8, cursor: deleting ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500 }}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ── DETAIL / EDIT / CREATE VIEW ── */
  const empName2 = selectedEmp ? `${selectedEmp.first_name || ''} ${selectedEmp.last_name || ''}`.trim() : 'New Employee';

  return (
    <div>
      {/* Sticky header: back button, header card, tabs */}
      <div className="emp-sticky-header">
        {/* Back button + page actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12 }}>
          <button
            className="btn btn-secondary"
            onClick={backToList}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <IconBack /> Back to List
          </button>

          {mode === 'view' && selectedEmp && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn"
                onClick={openLetterModal}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(59,130,246,0.15)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.3)', padding: '8px 16px', fontSize: 13, fontWeight: 500, borderRadius: 6, cursor: 'pointer' }}
              >
                <IconLetter /> HR Letters
              </button>
              <button
                className="btn btn-primary"
                onClick={switchToEdit}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <IconEdit /> Edit
              </button>
              <button
                className="btn"
                style={{ ...styles.actionBtnDanger, width: 'auto', padding: '8px 16px', fontSize: 13, fontWeight: 500, gap: 6, display: 'flex', alignItems: 'center' }}
                onClick={() => setDeleteConfirm(selectedEmp)}
              >
                <IconDelete /> Delete
              </button>
            </div>
          )}

          {isEditable && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {saving ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Create')}
              </button>
              <button className="btn btn-secondary"
                onClick={mode === 'create' ? backToList : () => { if (confirmIfDirty()) setMode('view'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Close
              </button>
            </div>
          )}
        </div>

        {/* Employee header card */}
        <div style={styles.headerCard}>
          <div
            style={{
              ...styles.avatar,
              position: 'relative', overflow: 'hidden', cursor: mode !== 'create' ? 'pointer' : 'default',
              ...(form.photo_path ? { background: 'none', border: '2px solid rgba(255,255,255,0.12)' } : {}),
            }}
            title={mode !== 'create' ? 'Click to upload photo' : ''}
            onClick={() => {
              if (mode === 'create') return;
              const inp = document.createElement('input');
              inp.type = 'file';
              inp.accept = 'image/jpeg,image/png,image/webp';
              inp.onchange = async (ev) => {
                const file = ev.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append('photo', file);
                try {
                  const res = await apiFetch(`/api/employees/${selectedEmp.id}/photo`, {
                    method: 'POST', body: fd, skipContentType: true,
                  });
                  if (res.ok) {
                    const data = await res.json();
                    setForm(prev => ({ ...prev, photo_path: data.photo_path + '?t=' + Date.now() }));
                    toast.success('Photo uploaded successfully')
                  } else {
                    const errData = await res.json().catch(() => ({}));
                    toast.error('Photo upload failed: ' + (errData.error || `HTTP ${res.status}`))
                  }
                } catch (e) {
                  console.error('Photo upload error:', e);
                  toast.error('Photo upload failed: ' + e.message)
                }
              };
              inp.click();
            }}
          >
            {form.photo_path ? (
              <img
                src={form.photo_path}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }}
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <span style={{
              display: form.photo_path ? 'none' : 'flex',
              alignItems: 'center', justifyContent: 'center',
              width: '100%', height: '100%', position: 'absolute', top: 0, left: 0,
            }}>
              {getInitials(form.first_name, form.last_name)}
            </span>
            {mode !== 'create' && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 20,
                background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: '#fff', opacity: 0.7, letterSpacing: '0.3px',
              }}>
                {form.photo_path ? 'Change' : 'Upload'}
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#F1F5F9' }}>
              {mode === 'create' ? 'New Employee' : empName2}
            </h2>
            <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {form.status && (
                <span className={`badge ${STATUS_BADGES[form.status] || 'badge-gray'}`}>
                  {form.status}
                </span>
              )}
              {form.emp_code && <span style={{ fontSize: 13, color: '#64748B' }}>#{form.emp_code}</span>}
              {form.department_id && <span style={{ fontSize: 13, color: '#64748B' }}>{deptName(form.department_id)}</span>}
              {form.designation_id && <span style={{ fontSize: 13, color: '#64748B' }}>{desigName(form.designation_id)}</span>}
              {form.location && <span style={{ fontSize: 13, color: '#64748B' }}>{form.location}</span>}
            </div>
          </div>
        </div>

        {/* Error banner */}
        {errors._general && (
          <div style={{ background: 'rgba(220,38,38,0.15)', color: '#FCA5A5', padding: '10px 14px',
            borderRadius: 6, marginTop: 12, fontSize: 13, border: '1px solid rgba(220,38,38,0.3)' }}>
            {errors._general}
          </div>
        )}

        {/* Scrollable Tab bar */}
        <div style={{ display: 'flex', alignItems: 'stretch', marginTop: 12, position: 'relative' }}>
          <button
            onClick={() => { if (tabScrollRef.current) tabScrollRef.current.scrollLeft -= 160; }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, flexShrink: 0,
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              borderRight: 'none', borderRadius: '12px 0 0 0', color: '#64748B', cursor: 'pointer' }}
          ><IconChevronLeft /></button>
          <div ref={tabScrollRef} style={{ ...styles.tabBar, flex: 1, borderRadius: 0, scrollBehavior: 'smooth',
            msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            {TAB_LIST.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={styles.tabBtn(activeTab === tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => { if (tabScrollRef.current) tabScrollRef.current.scrollLeft += 160; }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, flexShrink: 0,
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              borderLeft: 'none', borderRadius: '0 12px 0 0', color: '#64748B', cursor: 'pointer' }}
          ><IconChevronRight /></button>
        </div>
      </div>

      {/* Tab content */}
      <div style={styles.tabContent}>
        {detailLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>Loading employee details...</div>
        ) : (
          renderTabContent()
        )}
      </div>

      {/* Save / Cancel bar at bottom for edit/create (mirrors sticky header buttons) */}
      {isEditable && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16,
          borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Create')}
          </button>
          <button className="btn btn-secondary" onClick={mode === 'create' ? backToList : () => { if (confirmIfDirty()) setMode('view'); }}>
            Close
          </button>
        </div>
      )}

      {/* Delete confirmation overlay */}
      {deleteConfirm && (
        <div style={styles.deleteConfirm} onClick={() => !deleting && setDeleteConfirm(null)}>
          <div style={styles.deleteBox} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#F1F5F9' }}>Delete Employee</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#94A3B8', lineHeight: 1.5 }}>
              Are you sure you want to delete <strong style={{ color: '#E2E8F0' }}>
              {deleteConfirm.first_name} {deleteConfirm.last_name}</strong> ({deleteConfirm.emp_code || 'No ID'})?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: 'rgba(220,38,38,0.8)', color: '#fff', border: 'none', padding: '8px 20px',
                  borderRadius: 8, cursor: deleting ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500 }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HR Letter Modal ── */}
      {showLetterModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowLetterModal(false)}>
          <div style={{ background: '#1E293B', borderRadius: 12, width: '90%', maxWidth: 800, maxHeight: '85vh', overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#F1F5F9' }}>HR Letters</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94A3B8' }}>
                  {selectedEmp ? `${selectedEmp.first_name} ${selectedEmp.last_name}` : 'Employee'} — Generate, download, or email letters
                </p>
              </div>
              <button onClick={() => setShowLetterModal(false)}
                style={{ background: 'none', border: 'none', color: '#94A3B8', fontSize: 22, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>
                ×
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {letterLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Loading...</div>
              ) : (
                <>
                  {/* Upload new template section */}
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 16, marginBottom: 24 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <IconUpload /> Upload Template
                    </h3>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <label style={{ fontSize: 11, color: '#94A3B8', display: 'block', marginBottom: 4 }}>Template Name</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Appraisal Letter 2026"
                          value={newTemplate.name}
                          onChange={e => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                          style={{ fontSize: 13, padding: '7px 10px' }}
                        />
                      </div>
                      <div style={{ minWidth: 160 }}>
                        <label style={{ fontSize: 11, color: '#94A3B8', display: 'block', marginBottom: 4 }}>Category</label>
                        <select
                          className="form-control"
                          value={newTemplate.category}
                          onChange={e => setNewTemplate(prev => ({ ...prev, category: e.target.value }))}
                          style={{ fontSize: 13, padding: '7px 10px' }}
                        >
                          {LETTER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: '#94A3B8', display: 'block', marginBottom: 4 }}>Choose .docx File</label>
                        <input
                          type="file"
                          accept=".docx"
                          onChange={handleUploadTemplate}
                          disabled={uploadingTemplate}
                          style={{ fontSize: 12, color: '#CBD5E1' }}
                        />
                      </div>
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: 11, color: '#64748B' }}>
                      Use {'{placeholder}'} syntax in your .docx template. Available: {'{first_name}'}, {'{last_name}'}, {'{full_name}'}, {'{emp_code}'}, {'{email}'}, {'{designation}'}, {'{department}'}, {'{date_of_joining}'}, {'{company_name}'}, {'{today_date}'}, etc.
                    </p>
                  </div>

                  {/* Templates list */}
                  <div style={{ marginBottom: 24 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#E2E8F0' }}>
                      Available Templates ({letterTemplates.length})
                    </h3>
                    {letterTemplates.length === 0 ? (
                      <p style={{ color: '#64748B', fontSize: 13 }}>No templates uploaded yet. Upload a .docx template above.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {letterTemplates.map(tpl => {
                          let fieldCount = 0;
                          try { fieldCount = JSON.parse(tpl.placeholders || '[]').length; } catch(e) {}
                          return (
                            <div key={tpl.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#E2E8F0' }}>{tpl.name}</div>
                                <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                                  {tpl.category}{fieldCount > 0 ? ` — ${fieldCount} placeholder fields` : ''}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  onClick={() => handleGenerateLetter(tpl.id)}
                                  disabled={generatingTemplate === tpl.id}
                                  style={{ background: 'rgba(34,197,94,0.15)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.3)', padding: '5px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                                >
                                  {generatingTemplate === tpl.id ? 'Generating...' : 'Generate'}
                                </button>
                                <button
                                  onClick={() => handleDeleteTemplate(tpl.id)}
                                  style={{ background: 'rgba(220,38,38,0.12)', color: '#F87171', border: '1px solid rgba(220,38,38,0.25)', padding: '5px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer' }}
                                >
                                  <IconTrashSmall />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Generated letters */}
                  <div>
                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#E2E8F0' }}>
                      Generated Letters ({generatedLetters.length})
                    </h3>
                    {generatedLetters.length === 0 ? (
                      <p style={{ color: '#64748B', fontSize: 13 }}>No letters generated yet. Select a template above and click Generate.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {generatedLetters.map(letter => (
                          <div key={letter.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: '#E2E8F0' }}>
                                {letter.template_name || 'Letter'}{letter.template_category ? ` — ${letter.template_category}` : ''}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                                {new Date(letter.created_at).toLocaleString()}
                                {letter.status && (
                                  <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 3, fontSize: 10,
                                    background: letter.status === 'emailed' ? 'rgba(59,130,246,0.2)' : letter.status === 'approved' ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)',
                                    color: letter.status === 'emailed' ? '#60A5FA' : letter.status === 'approved' ? '#4ADE80' : '#94A3B8' }}>
                                    {letter.status}{letter.email_to ? ` → ${letter.email_to}` : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                onClick={() => handleDownloadLetter(letter.id)}
                                title="Download"
                                style={{ background: 'rgba(255,255,255,0.06)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)', padding: '5px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                              >
                                <IconDownload /> Download
                              </button>
                              <button
                                onClick={() => handleEmailLetter(letter.id)}
                                title="Email"
                                style={{ background: 'rgba(59,130,246,0.12)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.25)', padding: '5px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                              >
                                <IconMail /> Email
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
